import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useApi } from "../../../shared/api/api";
import AlertMessage from "../../../shared/components/AlertMessage";
import PaginationToolbar from "../../../shared/components/PaginationToolbar";
import { useAutoDismissMessage } from "../../../shared/hooks/useAutoDismissMessage";
import { formatDate } from "../../../shared/utils/format";
import { crearAsignacionFonemaVacia, crearObservacionLenguajeVacia, crearPacienteFonemaVacio, estaActivo } from "../../../dominio/catalogos";
import { calculateAge, crearPacienteFormulario, crearPacienteVacio, getBirthDate } from "../../../dominio/paciente";

const estadoClass = {
  Pendiente: "secondary",
  "En proceso": "warning",
  Completado: "success",
  "En seguimiento": "warning"
};

export default function PacientePerfil() {
  const { id } = useParams();
  const location = useLocation();
  const { apiFetch } = useApi();
  const [paciente, setPaciente] = useState(null);
  const [citas, setCitas] = useState([]);
  const [fonemas, setFonemas] = useState([]);
  const [catalogoFonemas, setCatalogoFonemas] = useState([]);
  const [catalogoProblemasComunicacion, setCatalogoProblemasComunicacion] = useState([]);
  const [observacionesLenguaje, setObservacionesLenguaje] = useState([]);
  const [mensaje, setMensaje] = useState(null);
  const [mostrarDatosPaciente, setMostrarDatosPaciente] = useState(true);
  const [showEditarPacienteModal, setShowEditarPacienteModal] = useState(false);
  const [showAsignarModal, setShowAsignarModal] = useState(false);
  const [showEditarFonemaModal, setShowEditarFonemaModal] = useState(false);
  const [showLenguajeModal, setShowLenguajeModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({
    visible: false,
    tipo: "",
    id: null,
    titulo: "",
    mensaje: "",
    cerrarEditarFonema: false
  });
  const [nuevoFonema, setNuevoFonema] = useState(crearAsignacionFonemaVacia);
  const [fonemaEditado, setFonemaEditado] = useState(crearPacienteFonemaVacio);
  const [nuevaObservacionLenguaje, setNuevaObservacionLenguaje] = useState(crearObservacionLenguajeVacia);
  const [pacienteEditado, setPacienteEditado] = useState(crearPacienteVacio);
  const [cargandoPerfil, setCargandoPerfil] = useState(true);
  const [cargandoCatalogoFonemas, setCargandoCatalogoFonemas] = useState(true);
  const [cargandoCatalogoProblemas, setCargandoCatalogoProblemas] = useState(true);
  const [paginaFonemas, setPaginaFonemas] = useState(1);
  const [registrosPorPaginaFonemas, setRegistrosPorPaginaFonemas] = useState(5);
  const [paginaCitas, setPaginaCitas] = useState(1);
  const [registrosPorPaginaCitas, setRegistrosPorPaginaCitas] = useState(5);

  useAutoDismissMessage(mensaje, () => setMensaje(null));

  const cargarPerfil = useCallback(async ({ mostrarCarga = false } = {}) => {
    if (mostrarCarga) {
      setCargandoPerfil(true);
      setCargandoCatalogoFonemas(true);
      setCargandoCatalogoProblemas(true);
    }

    const cargarDatosPaciente = async () => {
      try {
        const perfilData = await apiFetch(`/pacientePerfil.php?id=${id}`);
        setPaciente(perfilData?.paciente || null);
        setCitas(perfilData?.citas || []);
        setFonemas(perfilData?.fonemas || []);
        setObservacionesLenguaje(perfilData?.lenguajeComunicacion || []);
      } finally {
        setCargandoPerfil(false);
      }
    };

    const cargarCatalogoFonemas = async () => {
      try {
        const fonemasData = await apiFetch("/fonemas.php?action=listActivos");
        setCatalogoFonemas(Array.isArray(fonemasData) ? fonemasData : []);
      } finally {
        setCargandoCatalogoFonemas(false);
      }
    };

    const cargarCatalogoProblemas = async () => {
      try {
        const problemasComunicacionData = await apiFetch("/lenguajeComunicacion.php?action=listActivos", "GET", null, { silent: true });
        setCatalogoProblemasComunicacion(Array.isArray(problemasComunicacionData) ? problemasComunicacionData : []);
      } finally {
        setCargandoCatalogoProblemas(false);
      }
    };

    await Promise.allSettled([
      cargarDatosPaciente(),
      cargarCatalogoFonemas(),
      cargarCatalogoProblemas()
    ]);
  }, [apiFetch, id]);

  useEffect(() => {
    cargarPerfil({ mostrarCarga: true });
  }, [cargarPerfil]);

  useEffect(() => {
    setPaginaFonemas((pagina) =>
      Math.min(Math.max(1, pagina), Math.max(1, Math.ceil(fonemas.length / registrosPorPaginaFonemas)))
    );
  }, [fonemas.length, registrosPorPaginaFonemas]);

  useEffect(() => {
    setPaginaCitas((pagina) =>
      Math.min(Math.max(1, pagina), Math.max(1, Math.ceil(citas.length / registrosPorPaginaCitas)))
    );
  }, [citas.length, registrosPorPaginaCitas]);

  const progreso = useMemo(() => {
    const total = fonemas.length;
    const completados = fonemas.filter((fonema) => fonema.Estado === "Completado").length;
    const porcentaje = total > 0 ? Math.round((completados / total) * 100) : 0;

    return { total, completados, porcentaje };
  }, [fonemas]);

  const fonemasDisponibles = useMemo(
    () =>
      catalogoFonemas.filter(
        (catalogo) =>
          estaActivo(catalogo) &&
          !fonemas.some((asignado) => String(asignado.IdFonema) === String(catalogo.IdFonema))
      ),
    [catalogoFonemas, fonemas]
  );

  const problemasComunicacionDisponibles = useMemo(
    () => catalogoProblemasComunicacion.filter((problema) => estaActivo(problema)),
    [catalogoProblemasComunicacion]
  );

  const totalPaginasFonemas = Math.ceil(fonemas.length / registrosPorPaginaFonemas);
  const indiceInicioFonemas = (paginaFonemas - 1) * registrosPorPaginaFonemas;
  const fonemasPaginados = fonemas.slice(indiceInicioFonemas, indiceInicioFonemas + registrosPorPaginaFonemas);
  const totalPaginasCitas = Math.ceil(citas.length / registrosPorPaginaCitas);
  const indiceInicioCitas = (paginaCitas - 1) * registrosPorPaginaCitas;
  const citasPaginadas = citas.slice(indiceInicioCitas, indiceInicioCitas + registrosPorPaginaCitas);

  const abrirAsignarFonema = () => {
    setNuevoFonema(crearAsignacionFonemaVacia({
      IdFonema: fonemasDisponibles[0]?.IdFonema ? String(fonemasDisponibles[0].IdFonema) : "",
      Prioridad: "Media",
      Observaciones: ""
    }));
    setShowAsignarModal(true);
  };

  const handleNuevoFonemaChange = (e) => {
    const { name, value } = e.target;
    setNuevoFonema((actual) => ({ ...actual, [name]: value }));
  };

  const asignarFonema = async (e) => {
    e.preventDefault();
    const fonemaCatalogo = catalogoFonemas.find((fonema) => String(fonema.IdFonema) === String(nuevoFonema.IdFonema));

    const resp = await apiFetch("/pacienteFonemas.php?action=assign", "POST", {
      IdPaciente: id,
      IdFonema: nuevoFonema.IdFonema,
      Prioridad: nuevoFonema.Prioridad,
      Observaciones: nuevoFonema.Observaciones
    });

    if (resp?.ok === false) {
      setMensaje({ tipo: "danger", texto: resp?.error || "No se pudo asignar el fonema." });
      return;
    }

    if (fonemaCatalogo) {
      setFonemas((actuales) => [
        ...actuales,
        {
          ...fonemaCatalogo,
          IdPacienteFonema: resp?.IdPacienteFonema || resp?.id || `tmp-${Date.now()}`,
          Estado: "Pendiente",
          Prioridad: nuevoFonema.Prioridad,
          Observaciones: nuevoFonema.Observaciones || "Sin observaciones registradas.",
          FechaAsignacion: new Date().toISOString().slice(0, 10),
          FechaCompletado: null
        }
      ]);
    }

    setShowAsignarModal(false);
    setMensaje({ tipo: "success", texto: "Fonema asignado correctamente." });
  };

  const abrirEditarFonema = (fonema) => {
    setFonemaEditado(crearPacienteFonemaVacio({
      IdPacienteFonema: fonema.IdPacienteFonema,
      IdFonema: fonema.IdFonema,
      Simbolo: fonema.Simbolo,
      Nombre: fonema.Nombre,
      Estado: fonema.Estado,
      Prioridad: fonema.Prioridad,
      Observaciones: fonema.Observaciones || ""
    }));
    setShowEditarFonemaModal(true);
  };

  const guardarEdicionFonema = async (e) => {
    e.preventDefault();

    const resp = await apiFetch("/pacienteFonemas.php?action=update", "PUT", {
      IdPacienteFonema: fonemaEditado.IdPacienteFonema,
      Estado: fonemaEditado.Estado,
      Prioridad: fonemaEditado.Prioridad,
      Observaciones: fonemaEditado.Observaciones
    });

    if (resp?.ok === false) {
      setMensaje({ tipo: "danger", texto: resp?.error || "No se pudo actualizar el fonema." });
      return;
    }

    setFonemas((actuales) =>
      actuales.map((fonema) =>
        String(fonema.IdPacienteFonema) === String(fonemaEditado.IdPacienteFonema)
          ? {
              ...fonema,
              Estado: fonemaEditado.Estado,
              Prioridad: fonemaEditado.Prioridad,
              Observaciones: fonemaEditado.Observaciones || "Sin observaciones registradas.",
              FechaCompletado:
                fonemaEditado.Estado === "Completado"
                  ? fonema.FechaCompletado || new Date().toISOString().slice(0, 10)
                  : null
            }
          : fonema
      )
    );

    setShowEditarFonemaModal(false);
    setMensaje({ tipo: "success", texto: "Fonema actualizado correctamente." });
  };

  const pedirEliminarFonema = (idPacienteFonema, cerrarModal = false) => {
    const fonema = fonemas.find((item) => String(item.IdPacienteFonema) === String(idPacienteFonema));
    setConfirmDelete({
      visible: true,
      tipo: "fonema",
      id: idPacienteFonema,
      titulo: "Eliminar fonema",
      mensaje: `Deseas eliminar ${fonema?.Simbolo || "este fonema"} del perfil?`,
      cerrarEditarFonema: cerrarModal
    });
  };

  const abrirObservacionLenguaje = () => {
    const primerProblema = problemasComunicacionDisponibles[0];
    setNuevaObservacionLenguaje(crearObservacionLenguajeVacia({
      IdObservacion: null,
      IdArea: primerProblema?.IdArea || "",
      Area: primerProblema?.Nombre || "Lenguaje expresivo",
      Estado: "Pendiente",
      Texto: ""
    }));
    setShowLenguajeModal(true);
  };

  const abrirEditarObservacionLenguaje = (observacion) => {
    setNuevaObservacionLenguaje(crearObservacionLenguajeVacia({
      IdObservacion: observacion.IdObservacion,
      IdArea: observacion.IdArea || observacion.IdProblemaComunicacion || "",
      Area: observacion.Area,
      Estado: observacion.Estado,
      Texto: observacion.Texto
    }));
    setShowLenguajeModal(true);
  };

  const handleObservacionLenguajeChange = (e) => {
    const { name, value } = e.target;
    if (name === "IdArea") {
      const problema = problemasComunicacionDisponibles.find(
        (item) => String(item.IdArea) === String(value)
      );

      setNuevaObservacionLenguaje((actual) => ({
        ...actual,
        IdArea: value,
        Area: problema?.Nombre || actual.Area
      }));
      return;
    }

    setNuevaObservacionLenguaje((actual) => ({ ...actual, [name]: value }));
  };

  const guardarObservacionLenguaje = async (e) => {
    e.preventDefault();

    let resp;

    if (nuevaObservacionLenguaje.IdObservacion) {
      resp = await apiFetch("/pacienteLenguajeComunicacion.php?action=update", "PUT", nuevaObservacionLenguaje);
    } else {
      resp = await apiFetch("/pacienteLenguajeComunicacion.php?action=add", "POST", {
        IdPaciente: id,
        IdArea: nuevaObservacionLenguaje.IdArea,
        Area: nuevaObservacionLenguaje.Area,
        Estado: nuevaObservacionLenguaje.Estado,
        Texto: nuevaObservacionLenguaje.Texto
      });
    }

    if (resp?.ok === false) {
      setMensaje({ tipo: "danger", texto: resp?.error || "No se pudo guardar la observacion." });
      return;
    }

    if (nuevaObservacionLenguaje.IdObservacion) {
      setObservacionesLenguaje((actuales) =>
        actuales.map((observacion) =>
          String(observacion.IdObservacion) === String(nuevaObservacionLenguaje.IdObservacion)
            ? {
                ...observacion,
                Area: nuevaObservacionLenguaje.Area,
                IdArea: nuevaObservacionLenguaje.IdArea,
                Estado: nuevaObservacionLenguaje.Estado,
                Texto: nuevaObservacionLenguaje.Texto
              }
            : observacion
        )
      );
    } else {
      setObservacionesLenguaje((actuales) => [
        {
          IdObservacion: resp?.IdObservacion || resp?.id || `tmp-${Date.now()}`,
          IdPaciente: id,
          IdArea: nuevaObservacionLenguaje.IdArea,
          Area: nuevaObservacionLenguaje.Area,
          Estado: nuevaObservacionLenguaje.Estado,
          Texto: nuevaObservacionLenguaje.Texto,
          Fecha: new Date().toISOString().slice(0, 10)
        },
        ...actuales
      ]);
    }

    setShowLenguajeModal(false);
    setMensaje({
      tipo: "success",
      texto: nuevaObservacionLenguaje.IdObservacion
        ? "Observacion actualizada correctamente."
        : "Observacion agregada correctamente."
    });
  };

  const pedirEliminarObservacionLenguaje = (idObservacion) => {
    setConfirmDelete({
      visible: true,
      tipo: "lenguaje",
      id: idObservacion,
      titulo: "Eliminar observacion",
      mensaje: "Deseas eliminar esta observacion de lenguaje y comunicacion?",
      cerrarEditarFonema: false
    });
  };

  const cancelarEliminar = () => {
    setConfirmDelete({
      visible: false,
      tipo: "",
      id: null,
      titulo: "",
      mensaje: "",
      cerrarEditarFonema: false
    });
  };

  const confirmarEliminar = async () => {
    if (!confirmDelete.id) return;

    let resp;

    if (confirmDelete.tipo === "fonema") {
      resp = await apiFetch("/pacienteFonemas.php?action=delete", "POST", {
        IdPacienteFonema: confirmDelete.id
      });
    }

    if (confirmDelete.tipo === "lenguaje") {
      resp = await apiFetch("/pacienteLenguajeComunicacion.php?action=delete", "POST", {
        IdObservacion: confirmDelete.id
      });
    }

    if (resp?.ok === false) {
      setMensaje({ tipo: "danger", texto: resp?.error || "No se pudo eliminar el registro." });
      return;
    }

    const tipoEliminado = confirmDelete.tipo;

    if (confirmDelete.tipo === "fonema") {
      setFonemas((actuales) =>
        actuales.filter((fonema) => String(fonema.IdPacienteFonema) !== String(confirmDelete.id))
      );
    }

    if (confirmDelete.tipo === "lenguaje") {
      setObservacionesLenguaje((actuales) =>
        actuales.filter((observacion) => String(observacion.IdObservacion) !== String(confirmDelete.id))
      );
    }

    if (confirmDelete.cerrarEditarFonema) setShowEditarFonemaModal(false);

    cancelarEliminar();
    setMensaje({
      tipo: "success",
      texto: tipoEliminado === "fonema"
        ? "Fonema eliminado correctamente."
        : "Observacion eliminada correctamente."
    });
  };

  const formatFecha = (fecha) => formatDate(fecha, { withTime: String(fecha || "").includes(":") });

  const abrirEditarPaciente = () => {
    if (!paciente) return;

    setPacienteEditado(crearPacienteFormulario(paciente));
    setShowEditarPacienteModal(true);
  };

  const handlePacienteEditadoChange = (e) => {
    const { name, value } = e.target;
    setPacienteEditado((actual) => ({ ...actual, [name]: value }));
  };

  const guardarPacienteEditado = async (e) => {
    e.preventDefault();

    const payload = {
      ...pacienteEditado,
      Estado: Number(pacienteEditado.Estado)
    };

    const resp = await apiFetch("/pacientes.php?action=update", "POST", payload);

    if (resp?.ok === false) {
      setMensaje({ tipo: "danger", texto: resp?.error || "No se pudo actualizar el paciente." });
      return;
    }

    setPaciente((actual) => ({
      ...actual,
      ...payload,
      EncargadoLegalNombreCompleto: payload.EncargadoLegalNombreCompleto,
      FechaNacimiento: payload.FechaNacimiento
    }));
    setShowEditarPacienteModal(false);
    setMensaje({ tipo: "success", texto: "Paciente actualizado correctamente." });
  };

  const nombreCompletoPaciente = paciente?.Nombre || "Paciente";
  const encargadoLegal = paciente?.EncargadoLegalNombreCompleto ?? "N/A";
  const fechaNacimiento = getBirthDate(paciente);
  const inicial = nombreCompletoPaciente?.trim()?.charAt(0)?.toUpperCase() || "P";
  const volverA = location.state?.from || "/pacientes";
  const volverLabel = location.state?.label || "pacientes";
  const cargandoCatalogo = cargandoCatalogoFonemas || cargandoCatalogoProblemas;

  if (!cargandoPerfil && !paciente) {
    return (
      <div className="container-fluid mt-3 px-3 px-md-4">
        <div className="alert alert-warning">
          No se encontro el paciente solicitado o el endpoint de perfil aun no devolvio datos.
        </div>
        <Link className="btn btn-secondary btn-sm" to={volverA}>
          Volver a {volverLabel}
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3 px-3 px-md-4 patient-profile">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-3 mb-3">
        <div>
          <Link className="btn btn-outline-secondary btn-sm mb-3" to={volverA}>
            Volver
          </Link>
          <div className="d-flex align-items-center gap-3">
            <div className="profile-avatar">{inicial}</div>
            <div>
              <h2 className="mb-1">{nombreCompletoPaciente}</h2>
              <div className="text-muted">
                {cargandoPerfil
                  ? "Cargando datos del paciente..."
                  : `Paciente #${paciente.IdPaciente} - ${paciente.Estado === 1 ? "Activo" : "Inactivo"}`}
              </div>
            </div>
          </div>
        </div>

        <div className="patient-progress-panel">
          <span className="text-muted small">Progreso fonologico</span>
          <strong>{cargandoPerfil ? "..." : `${progreso.porcentaje}%`}</strong>
          <div className="progress" role="progressbar" aria-valuenow={progreso.porcentaje} aria-valuemin="0" aria-valuemax="100">
            <div className="progress-bar" style={{ width: `${progreso.porcentaje}%` }}></div>
          </div>
          <small>
            {cargandoPerfil
              ? "Cargando progreso..."
              : `${progreso.completados} de ${progreso.total} fonemas completados`}
          </small>
        </div>
      </div>

      <AlertMessage message={mensaje} onClose={() => setMensaje(null)} />

      <div className="row g-3 mb-3">
        <div className="col-12 col-lg-4">
          <div className={`card ${mostrarDatosPaciente ? "h-100" : "patient-details-card-compact"}`}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                <h5 className="card-title mb-0">Datos del paciente</h5>
                <div className="d-flex gap-2">
                  {mostrarDatosPaciente && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      type="button"
                      onClick={abrirEditarPaciente}
                      disabled={cargandoPerfil || !paciente}
                    >
                      Editar
                    </button>
                  )}
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    type="button"
                    onClick={() => setMostrarDatosPaciente((actual) => !actual)}
                    disabled={cargandoPerfil}
                  >
                    {mostrarDatosPaciente ? "Ocultar" : "Mostrar"}
                  </button>
                </div>
              </div>
              {cargandoPerfil ? (
                <div className="alert alert-info mb-0">Cargando datos del paciente...</div>
              ) : !mostrarDatosPaciente ? (
                <div className="alert alert-secondary mb-0">Datos del paciente ocultos.</div>
              ) : (
                <dl className="patient-detail-list mb-0">
                  <dt>Nombre completo</dt>
                  <dd>{nombreCompletoPaciente}</dd>
                  <dt>Encargado legal</dt>
                  <dd>{encargadoLegal || "N/A"}</dd>
                  <dt>Telefono</dt>
                  <dd>{paciente.Telefono || "N/A"}</dd>
                  <dt>Email</dt>
                  <dd>{paciente.Email || "N/A"}</dd>
                  <dt>Fecha de nacimiento</dt>
                  <dd>{formatFecha(fechaNacimiento)}</dd>
                  <dt>Edad</dt>
                  <dd>{calculateAge(fechaNacimiento, new Date(), "anos")}</dd>
                </dl>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                <h5 className="card-title mb-0">Fonemas asignados</h5>
                <button className="btn btn-success btn-sm" type="button" onClick={abrirAsignarFonema} disabled={cargandoPerfil || cargandoCatalogoFonemas || fonemasDisponibles.length === 0}>
                  Asignar fonema
                </button>
              </div>
              {cargandoPerfil ? (
                <div className="alert alert-info mb-0">Cargando fonemas asignados...</div>
              ) : fonemas.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle phoneme-list mb-0">
                      <thead>
                        <tr>
                          <th>Fonema</th>
                          <th>Trabajo</th>
                          <th>Prioridad</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fonemasPaginados.map((fonema) => (
                          <tr key={fonema.IdPacienteFonema}>
                            <td>
                              <span className="phoneme-symbol phoneme-symbol-compact">{fonema.Simbolo}</span>
                            </td>
                            <td>
                              <strong>{fonema.Nombre}</strong>
                              <div className="text-muted small">{fonema.Categoria}</div>
                              <div className="small phoneme-note">{fonema.Observaciones}</div>
                            </td>
                            <td>{fonema.Prioridad}</td>
                            <td>
                              <span className={`badge bg-${estadoClass[fonema.Estado] || "secondary"}`}>
                                {fonema.Estado}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                <button className="btn btn-outline-primary btn-sm" type="button" title="Editar fonema" onClick={() => abrirEditarFonema(fonema)}>
                                  &#9998;
                                </button>
                                <button className="btn btn-outline-danger btn-sm" type="button" title="Eliminar fonema" onClick={() => pedirEliminarFonema(fonema.IdPacienteFonema)}>
                                  &#10005;
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationToolbar
                    className="patient-profile-pagination mt-3"
                    page={paginaFonemas}
                    totalPages={totalPaginasFonemas}
                    pageSize={registrosPorPaginaFonemas}
                    pageSizes={[5, 10, 25]}
                    onPageChange={setPaginaFonemas}
                    onPageSizeChange={(size) => {
                      setRegistrosPorPaginaFonemas(size);
                      setPaginaFonemas(1);
                    }}
                  />
                </>
              ) : (
                <div className="alert alert-info mb-0">No hay fonemas asignados para este paciente.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
            <h5 className="card-title mb-0">Lenguaje y comunicacion</h5>
            <button className="btn btn-success btn-sm" type="button" onClick={abrirObservacionLenguaje} disabled={cargandoPerfil || cargandoCatalogoProblemas}>
              Agregar observacion
            </button>
          </div>

          {cargandoPerfil ? (
            <div className="alert alert-info mb-0">Cargando lenguaje y comunicacion...</div>
          ) : observacionesLenguaje.length > 0 ? (
            <div className="language-observation-list">
              {observacionesLenguaje.map((observacion) => (
                <div className="language-observation-item" key={observacion.IdObservacion}>
                  <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                    <div>
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <strong>{observacion.Area}</strong>
                        <span className={`badge bg-${estadoClass[observacion.Estado] || "secondary"}`}>
                          {observacion.Estado}
                        </span>
                        <span className="text-muted small">{formatFecha(observacion.Fecha)}</span>
                      </div>
                      <p className="mb-0 small">{observacion.Texto}</p>
                    </div>
                    <div className="d-flex align-items-start gap-2">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        type="button"
                        title="Editar observacion"
                        onClick={() => abrirEditarObservacionLenguaje(observacion)}
                      >
                        &#9998;
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        type="button"
                        title="Eliminar observacion"
                        onClick={() => pedirEliminarObservacionLenguaje(observacion.IdObservacion)}
                      >
                        &#10005;
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="alert alert-info mb-0">
              No hay observaciones de lenguaje y comunicacion registradas.
            </div>
          )}
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-body">
          <h5 className="card-title">Historial de citas</h5>
          {cargandoPerfil ? (
            <div className="alert alert-info mb-0">Cargando historial de citas...</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped table-sm align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Fecha</th>
                      <th>Edad</th>
                      <th>Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {citas.length > 0 ? (
                      citasPaginadas.map((cita) => (
                        <tr key={cita.IdCita}>
                          <td>{formatFecha(cita.Fecha)}</td>
                          <td>{calculateAge(fechaNacimiento, cita.Fecha)}</td>
                          <td>{cita.TipoCitaNombre || cita.IdTiposCita}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">No hay citas registradas para este paciente</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {citas.length > 0 && (
                <PaginationToolbar
                  className="patient-profile-pagination mt-3"
                  page={paginaCitas}
                  totalPages={totalPaginasCitas}
                  pageSize={registrosPorPaginaCitas}
                  pageSizes={[5, 10, 25]}
                  onPageChange={setPaginaCitas}
                  onPageSizeChange={(size) => {
                    setRegistrosPorPaginaCitas(size);
                    setPaginaCitas(1);
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {showEditarPacienteModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar paciente</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditarPacienteModal(false)}></button>
              </div>
              <div className="modal-body">
                <form className="row g-3" onSubmit={guardarPacienteEditado}>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Nombre completo del paciente</label>
                    <input
                      type="text"
                      name="Nombre"
                      className="form-control"
                      value={pacienteEditado.Nombre}
                      onChange={handlePacienteEditadoChange}
                      required
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Nombre completo del encargado legal</label>
                    <input
                      type="text"
                      name="EncargadoLegalNombreCompleto"
                      className="form-control"
                      value={pacienteEditado.EncargadoLegalNombreCompleto}
                      onChange={handlePacienteEditadoChange}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Telefono</label>
                    <input
                      type="text"
                      name="Telefono"
                      className="form-control"
                      value={pacienteEditado.Telefono}
                      onChange={handlePacienteEditadoChange}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="Email"
                      className="form-control"
                      value={pacienteEditado.Email}
                      onChange={handlePacienteEditadoChange}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Fecha de nacimiento</label>
                    <input
                      type="date"
                      name="FechaNacimiento"
                      className="form-control"
                      value={pacienteEditado.FechaNacimiento}
                      onChange={handlePacienteEditadoChange}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Estado</label>
                    <select
                      name="Estado"
                      className="form-select"
                      value={pacienteEditado.Estado}
                      onChange={handlePacienteEditadoChange}
                    >
                      <option value="1">Activo</option>
                      <option value="0">Inactivo</option>
                    </select>
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowEditarPacienteModal(false)}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-success btn-sm">
                      Actualizar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAsignarModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Asignar fonema</h5>
                <button type="button" className="btn-close" onClick={() => setShowAsignarModal(false)}></button>
              </div>
              <div className="modal-body">
                <form className="row g-3" onSubmit={asignarFonema}>
                  <div className="col-12">
                    <label className="form-label">Fonema</label>
                    <select
                      className="form-select"
                      name="IdFonema"
                      value={nuevoFonema.IdFonema}
                      onChange={handleNuevoFonemaChange}
                      required
                    >
                      {cargandoCatalogoFonemas && <option value="">Cargando fonemas...</option>}
                      {!cargandoCatalogoFonemas && fonemasDisponibles.map((fonema) => (
                        <option key={fonema.IdFonema} value={fonema.IdFonema}>
                          {fonema.Simbolo} - {fonema.Nombre} ({fonema.Categoria})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-5">
                    <label className="form-label">Prioridad</label>
                    <select
                      className="form-select"
                      name="Prioridad"
                      value={nuevoFonema.Prioridad}
                      onChange={handleNuevoFonemaChange}
                    >
                      <option value="Alta">Alta</option>
                      <option value="Media">Media</option>
                      <option value="Baja">Baja</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Observaciones</label>
                    <textarea
                      className="form-control"
                      name="Observaciones"
                      rows="3"
                      value={nuevoFonema.Observaciones}
                      onChange={handleNuevoFonemaChange}
                      placeholder="Notas iniciales del trabajo para este fonema"
                    ></textarea>
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAsignarModal(false)}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-success btn-sm" disabled={!nuevoFonema.IdFonema}>
                      Asignar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditarFonemaModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar fonema</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditarFonemaModal(false)}></button>
              </div>
              <div className="modal-body">
                <form className="row g-3" onSubmit={guardarEdicionFonema}>
                  <div className="col-12">
                    <div className="phoneme-edit-heading">
                      <span className="phoneme-symbol phoneme-symbol-compact">{fonemaEditado.Simbolo}</span>
                      <strong>{fonemaEditado.Nombre}</strong>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Estado</label>
                    <select
                      className="form-select"
                      value={fonemaEditado.Estado}
                      onChange={(e) => setFonemaEditado((actual) => ({ ...actual, Estado: e.target.value }))}
                    >
                      <option value="Pendiente">Pendiente</option>
                      <option value="En proceso">En proceso</option>
                      <option value="Completado">Completado</option>
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Prioridad</label>
                    <select
                      className="form-select"
                      value={fonemaEditado.Prioridad}
                      onChange={(e) => setFonemaEditado((actual) => ({ ...actual, Prioridad: e.target.value }))}
                    >
                      <option value="Alta">Alta</option>
                      <option value="Media">Media</option>
                      <option value="Baja">Baja</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Observaciones</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={fonemaEditado.Observaciones}
                      onChange={(e) => setFonemaEditado((actual) => ({ ...actual, Observaciones: e.target.value }))}
                      placeholder="Agrega notas del trabajo, avances o indicaciones"
                    ></textarea>
                  </div>
                  <div className="col-12 d-flex flex-column flex-sm-row justify-content-between gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => pedirEliminarFonema(fonemaEditado.IdPacienteFonema, true)}
                    >
                      Eliminar fonema
                    </button>
                    <div className="d-flex justify-content-end gap-2">
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowEditarFonemaModal(false)}>
                        Cancelar
                      </button>
                      <button type="submit" className="btn btn-success btn-sm">
                        Guardar
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLenguajeModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {nuevaObservacionLenguaje.IdObservacion ? "Editar observacion" : "Observacion de lenguaje y comunicacion"}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowLenguajeModal(false)}></button>
              </div>
              <div className="modal-body">
                <form className="row g-3" onSubmit={guardarObservacionLenguaje}>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Problema de comunicacion</label>
                    <select
                      className="form-select"
                      name="IdArea"
                      value={nuevaObservacionLenguaje.IdArea}
                      onChange={handleObservacionLenguajeChange}
                      disabled={Boolean(nuevaObservacionLenguaje.IdObservacion)}
                    >
                      {cargandoCatalogo && <option value="">Cargando opciones...</option>}
                      {!cargandoCatalogo && problemasComunicacionDisponibles.map((problema) => (
                        <option key={problema.IdArea} value={problema.IdArea}>
                          {problema.Nombre}
                        </option>
                      ))}
                      {nuevaObservacionLenguaje.IdObservacion && !nuevaObservacionLenguaje.IdArea && (
                        <option value="">{nuevaObservacionLenguaje.Area}</option>
                      )}
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Estado</label>
                    <select
                      className="form-select"
                      name="Estado"
                      value={nuevaObservacionLenguaje.Estado}
                      onChange={handleObservacionLenguajeChange}
                    >
                      <option value="Pendiente">Pendiente</option>
                      <option value="En seguimiento">En seguimiento</option>
                      <option value="Completado">Completado</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Observacion</label>
                    <textarea
                      className="form-control"
                      name="Texto"
                      rows="4"
                      value={nuevaObservacionLenguaje.Texto}
                      onChange={handleObservacionLenguajeChange}
                      placeholder="Describe la observacion, avance, conducta comunicativa o recomendacion"
                      required
                    ></textarea>
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowLenguajeModal(false)}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-success btn-sm">
                      Guardar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDelete.visible && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{confirmDelete.titulo}</h5>
                <button type="button" className="btn-close" onClick={cancelarEliminar}></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">{confirmDelete.mensaje}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={cancelarEliminar}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-danger btn-sm" onClick={confirmarEliminar}>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
